// ============================================================
// Chess Arena — Battle Scene
// ============================================================
// The main gameplay Phaser Scene. Thin orchestrator that owns
// the state machine, board rendering, and input handling.
// All game logic is delegated to extracted subsystems.
//
// State machine: SETUP → INTRO_TIPS → PLAYING → CHECKMATE → RESULT
// ============================================================

import Phaser from 'phaser';
import {
  GridPos,
  PieceType,
  PieceState,
  Team,
  LevelConfig,
  TutorialLevelConfig,
  BOARD_COLS,
  BOARD_ROWS,
  GameSettingsConfig,
  PIECE_CONFIGS,
} from '../core/types';
import { Board } from '../core/board';
import { Piece } from '../core/piece';
import { getValidMoves } from '../core/moveValidator';
import { InputHandler } from '../core/inputHandler';
import { TerrainSystem } from '../systems/terrainSystem';
import { TutorialManager } from '../systems/tutorialManager';
import { ToolManager } from '../systems/toolManager';
import { AbilityManager } from '../systems/abilityManager';
import { MovementLineManager } from '../systems/movementLineManager';
import { MatchTimer } from '../systems/matchTimer';
import { SpawnSystem } from '../systems/spawnSystem';
import { WaveSystem } from '../systems/waveSystem';
import { AIController } from '../systems/aiController';
import { AIDifficultyConfig, DIFFICULTY_PRESETS } from '../systems/minimaxSearch';
import { PieceManager } from '../systems/pieceManager';
import { AbilityEffectHandler } from '../systems/abilityEffectHandler';
import { SpotlightResolver } from '../systems/spotlightResolver';
import { BattleEventWiring } from '../systems/battleEventWiring';
import { BattleHUD } from './battleHUD';
import eventBus from '../core/eventBus';
import { playerState } from '../core/playerState';

// --- Battle state machine ---

type BattleState = 'SETUP' | 'INTRO_TIPS' | 'PLAYING' | 'CHECKMATE' | 'RESULT';

// --- Constants ---

// Board aligned to bg_battle_arena.png tile grid.
// Image tiles are non-square: ~91w x 81h in game px (measured from screenshot).
// CELL_SIZE (=CELL_W) controls column width; CELL_H controls row height.
const CELL_SIZE = 114;  // horizontal cell width (also used for sprite sizing)
const CELL_H = 113;     // vertical cell height
const TOP_BAR_H = 100;
const BOTTOM_BAR_H = 200;

// Board origin — measured from bg_battle_arena.png tile grid
const BG_BOARD_ORIGIN_X = 75;
const BG_BOARD_ORIGIN_Y = 179;

export class BattleScene extends Phaser.Scene {
  // State machine
  private battleState: BattleState = 'SETUP';

  // Level data
  private levelConfig!: LevelConfig;
  private gameSettings!: GameSettingsConfig;

  // Core systems
  private board!: Board;
  private terrainSystem!: TerrainSystem;
  private hud!: BattleHUD;
  private tutorialManager!: TutorialManager;
  private toolManager!: ToolManager;
  private abilityManager!: AbilityManager;
  private movementLines!: MovementLineManager;

  // Board rendering offsets (center the 5x7 board on screen)
  private boardOriginX = 0;
  private boardOriginY = 0;

  // Extracted systems
  private pieceManager!: PieceManager;
  private matchTimer!: MatchTimer;
  private spawnSystem!: SpawnSystem;
  private waveSystem!: WaveSystem;
  private aiController!: AIController;
  private abilityEffectHandler!: AbilityEffectHandler;
  private spotlightResolver!: SpotlightResolver;
  private eventWiring!: BattleEventWiring;

  // Army cap
  private armyCap = 8;

  // Elapsed time (orchestrator-owned, passed to subsystems)
  private elapsedTimeSec = 0;

  // Input handler (extracted)
  private inputHandler!: InputHandler;

  // Intro tips tracking
  private introTipIndex = 0;

  // Result
  private matchResult: 'victory' | 'defeat' | null = null;

  // Tutorial tracking
  private bottomBarRevealed = false;

  constructor() {
    super({ key: 'BattleScene' });
  }

  // ------------------------------------------------------------------
  // Phaser lifecycle
  // ------------------------------------------------------------------

  init(data: { levelConfig: LevelConfig; tutorialConfig?: TutorialLevelConfig }): void {
    // Clean up stale event handlers from a previous scene run
    this.eventWiring?.cleanup();
    this.tutorialManager?.destroy();

    this.levelConfig = data.levelConfig;
    this.battleState = 'SETUP';
    this.matchResult = null;
    this.elapsedTimeSec = 0;
    this.introTipIndex = 0;
  }

  create(): void {
    // Bind shutdown to Phaser lifecycle so cleanup runs when scene stops
    this.events.once('shutdown', this.shutdown, this);

    // Load game settings from cache
    this.gameSettings = this.cache.json.get('gameSettings') as GameSettingsConfig;

    // Army cap: per-level override > global setting > default 8
    this.armyCap = this.levelConfig.armyCap ?? this.gameSettings.armyCap ?? 8;

    this.setupBoard();
    this.setupTerrain();
    this.setupPieceManager();
    this.pieceManager.placeStartingPieces(this.levelConfig);
    this.setupMatchTimer();
    this.setupSpawnSystem();
    this.setupWaveSystem();
    this.setupAI();
    this.setupHUD();
    this.setupTutorial();
    this.setupAbilityEffectHandler();
    this.setupSpotlightResolver();
    this.setupEventWiring();
    this.setupInput();

    // Transition to next state
    if (this.levelConfig.introTips.length > 0) {
      this.transitionTo('INTRO_TIPS');
    } else if (this.levelConfig.tutorialLevel) {
      this.transitionTo('PLAYING');
      this.hud.setBottomBarVisible(false);
      this.bottomBarRevealed = false;
      this.tutorialManager.startTutorial();
    } else {
      this.transitionTo('PLAYING');
    }
  }

  update(_time: number, delta: number): void {
    switch (this.battleState) {
      case 'PLAYING':
        this.abilityEffectHandler.resetTargetingFrame();
        this.updatePlaying(delta);
        break;
      case 'INTRO_TIPS':
        // Waiting for player tap — handled by event
        break;
      case 'CHECKMATE':
      case 'RESULT':
        // No update logic needed
        break;
    }
  }

  // ------------------------------------------------------------------
  // State transitions
  // ------------------------------------------------------------------

  private transitionTo(newState: BattleState): void {
    this.battleState = newState;

    switch (newState) {
      case 'INTRO_TIPS':
        this.showNextIntroTip();
        break;
      case 'PLAYING':
        this.matchTimer.resume();
        break;
      case 'CHECKMATE':
        this.onCheckmate();
        break;
      case 'RESULT':
        this.onResult();
        break;
    }
  }

  // ------------------------------------------------------------------
  // Board setup
  // ------------------------------------------------------------------

  private setupBoard(): void {
    this.board = new Board();
    this.board.initBoard(this.levelConfig);

    if (this.textures.exists('bg_battle_arena')) {
      this.boardOriginX = BG_BOARD_ORIGIN_X;
      this.boardOriginY = BG_BOARD_ORIGIN_Y;
    } else {
      const { width, height } = this.cameras.main;
      const boardPixelW = BOARD_COLS * CELL_SIZE;
      const boardPixelH = BOARD_ROWS * CELL_H;
      this.boardOriginX = (width - boardPixelW) / 2;
      const availableH = height - TOP_BAR_H - BOTTOM_BAR_H;
      this.boardOriginY = TOP_BAR_H + (availableH - boardPixelH) / 2;
    }

    this.movementLines = new MovementLineManager(this, CELL_SIZE, CELL_H, this.boardOriginX, this.boardOriginY);

    this.drawArenaBackground();
  }

  // ------------------------------------------------------------------
  // Arena background — single themed image covering board + environment
  // ------------------------------------------------------------------

  private drawArenaBackground(): void {
    const { width, height } = this.cameras.main;

    if (this.textures.exists('bg_battle_arena')) {
      const bg = this.add.image(width / 2, height / 2, 'bg_battle_arena');
      bg.setDisplaySize(width, height);
      bg.setDepth(-10);
    } else {
      const g = this.add.graphics();
      g.setDepth(-10);
      g.fillStyle(0x181410, 1);
      g.fillRect(0, 0, width, height);
    }

  }

  // ------------------------------------------------------------------
  // Terrain
  // ------------------------------------------------------------------

  private setupTerrain(): void {
    this.terrainSystem = new TerrainSystem(this);
    const settings = this.cache.json.get('gameSettings');
    if (settings) {
      this.terrainSystem.loadTerrainConfigs(settings);
    }
    this.terrainSystem.setBoardLayout(this.boardOriginX, this.boardOriginY, CELL_SIZE, CELL_H);

    if (this.levelConfig.level === 3 && this.levelConfig.tutorialLevel) {
      // Tutorial Level 3 (premove): fixed rock for rock intro step
      this.terrainSystem.addTerrain('rock', { col: 2, row: 3 });
    } else if (this.levelConfig.level === 4 && this.levelConfig.tutorialLevel) {
      // Tutorial Level 4 (bush): fixed bush placement for scripted ambush (5x7)
      this.terrainSystem.addTerrain('bush', { col: 3, row: BOARD_ROWS - 3 });
    } else {
      this.terrainSystem.placeTerrainProcedurally(
        this.levelConfig.terrain.maxRocks,
        this.levelConfig.terrain.maxBushes,
      );
    }

    // Sync terrain positions from TerrainSystem to Board so move validation
    // uses the same rock/bush positions that are rendered visually.
    for (const tile of this.terrainSystem.getAllTerrain()) {
      this.board.setTerrain(tile.pos, tile.type);
    }
  }

  // ------------------------------------------------------------------
  // Piece manager (extracted → PieceManager)
  // ------------------------------------------------------------------

  private setupPieceManager(): void {
    this.pieceManager = new PieceManager();
    this.pieceManager.init({
      scene: this,
      board: this.board,
      movementLines: this.movementLines,
      cellSize: CELL_SIZE,
      cellH: CELL_H,
      boardOriginX: this.boardOriginX,
      boardOriginY: this.boardOriginY,
      onMatchResult: (result) => {
        this.matchResult = result;
        this.transitionTo('CHECKMATE');
      },
    });
  }

  // ------------------------------------------------------------------
  // Match timer (extracted → MatchTimer)
  // ------------------------------------------------------------------

  private setupMatchTimer(): void {
    this.matchTimer = new MatchTimer(this);
    this.matchTimer.init(this.levelConfig.matchTimerSec);
    // Timer expiry handling is wired in BattleEventWiring
  }

  // ------------------------------------------------------------------
  // Spawn system (extracted → SpawnSystem)
  // ------------------------------------------------------------------

  /** Is the team at its army cap? */
  private isArmyFull(team: Team): boolean {
    return this.pieceManager.countAlivePieces(team) >= this.armyCap;
  }

  private setupSpawnSystem(): void {
    this.spawnSystem = new SpawnSystem(this);
    this.spawnSystem.init(this.levelConfig, {
      isCellAvailable: (pos: GridPos) => {
        const team: Team = pos.row >= Math.floor(BOARD_ROWS / 2) ? 'player' : 'enemy';
        if (this.isArmyFull(team)) return false;
        return this.board.isPassable(pos) && !this.board.getCellContent(pos)?.pieceId;
      },
      placePiece: (type: PieceType, team: Team, pos: GridPos) => {
        const cooldown = team === 'enemy' ? PIECE_CONFIGS[type].cooldown : 0;
        return this.pieceManager.createPiece(type, team, pos, cooldown).pieceState.id;
      },
    });
  }

  // ------------------------------------------------------------------
  // Wave system (extracted → WaveSystem)
  // ------------------------------------------------------------------

  private setupWaveSystem(): void {
    this.waveSystem = new WaveSystem(this);
    this.waveSystem.init(this.levelConfig, {
      isCellAvailable: (pos: GridPos) => {
        if (this.isArmyFull('enemy')) return false;
        return this.board.isPassable(pos) && !this.board.getCellContent(pos)?.pieceId;
      },
      placePiece: (type: PieceType, team: Team, pos: GridPos) => {
        const cooldown = PIECE_CONFIGS[type].cooldown;
        return this.pieceManager.createPiece(type, team, pos, cooldown).pieceState.id;
      },
    });
  }

  // ------------------------------------------------------------------
  // AI controller (extracted → AIController)
  // ------------------------------------------------------------------

  private setupAI(): void {
    // Build difficulty config from level settings
    const aiCfg = this.levelConfig.aiDifficulty;
    const preset = DIFFICULTY_PRESETS[aiCfg?.preset ?? 'medium'] ?? DIFFICULTY_PRESETS.medium;
    const difficulty: Partial<AIDifficultyConfig> = { ...preset };
    if (aiCfg?.depth !== undefined) difficulty.depth = aiCfg.depth;
    if (aiCfg?.evalNoise !== undefined) difficulty.evalNoise = aiCfg.evalNoise;
    if (aiCfg?.blunderRate !== undefined) difficulty.blunderRate = aiCfg.blunderRate;
    if (aiCfg?.moveInterval !== undefined) difficulty.moveInterval = aiCfg.moveInterval;
    if (aiCfg?.movesPerTick !== undefined) difficulty.movesPerTick = aiCfg.movesPerTick;

    this.aiController = new AIController();
    this.aiController.init({
      getPieces: (team: Team) => this.pieceManager.getPieces(team),
      getValidMoves: (piece: PieceState) => getValidMoves(piece, this.board, this.pieceManager.getAllPieceStates()),
      getPieceAt: (pos: GridPos) => {
        const cell = this.board.getCellContent(pos);
        if (!cell?.pieceId) return null;
        const p = this.pieceManager.getPieceById(cell.pieceId);
        return p?.pieceState.isAlive ? p.pieceState : null;
      },
      movePiece: (pieceId: string, target: GridPos) => {
        const piece = this.pieceManager.getPieceById(pieceId);
        if (piece && piece.pieceState.isAlive) {
          this.pieceManager.executeMove(piece, target);
        }
      },
      isBush: (pos: GridPos) => this.board.isBush(pos),
      areEnemiesFrozen: () =>
        !!(this.tutorialManager && this.tutorialManager.isActive() && this.tutorialManager.areEnemiesFrozen()),
      getTerrainGrid: () => this.board.getTerrainGrid(),
    }, difficulty);
  }

  // ------------------------------------------------------------------
  // Main update loop (PLAYING state)
  // ------------------------------------------------------------------

  private updatePlaying(delta: number): void {
    const deltaSec = delta / 1000;
    this.elapsedTimeSec += deltaSec;

    // During active tutorial, pause spawning, waves, and timer
    const tutorialActive = this.tutorialManager && this.tutorialManager.isActive();

    // Update piece cooldowns (pause when tutorial requests it)
    const cooldownsPaused = tutorialActive && this.tutorialManager!.areCooldownsPaused();
    if (!cooldownsPaused) {
      this.pieceManager.updateCooldowns(delta);
    }
    if (!tutorialActive) {
      this.spawnSystem.update(delta);
      this.waveSystem.update(this.elapsedTimeSec);
    }

    this.aiController.update(deltaSec);
    this.pieceManager.updateCollisions();

    // Update ability effects (freeze/shield timers)
    this.abilityEffectHandler.updateAbilityEffects(this.elapsedTimeSec);

    // Update match timer (extracted — pauses itself, but pause during tutorial)
    if (!tutorialActive) {
      this.matchTimer.update(delta);
    }

    // Reveal bottom bar when reinforcements or ability tutorial step is reached
    if (tutorialActive && !this.bottomBarRevealed) {
      const step = this.tutorialManager!.getCurrentStep();
      if (step && (
        step.stepId === 'reinforcements' ||
        step.stepId.startsWith('bomb_') ||
        step.spotlightTarget?.startsWith('abilityButton_')
      )) {
        this.bottomBarRevealed = true;
        this.hud.setBottomBarVisible(true);
      }
    }
    if (!tutorialActive && !this.bottomBarRevealed) {
      this.bottomBarRevealed = true;
      this.hud.setBottomBarVisible(true);
    }

    // Show persistent valid-move highlights for action-gated tutorial steps
    this.spotlightResolver.updateTutorialMoveHighlights(tutorialActive);

    // Update HUD — delegate queries to extracted systems
    const spawnProgress = this.spawnSystem.getBarProgress('player');
    const spawnTimeRemaining = this.levelConfig.playerSpawn
      ? Math.max(0, this.levelConfig.playerSpawn.timerSeconds * (1 - spawnProgress))
      : 0;

    this.hud.update(
      spawnProgress,
      this.spawnSystem.getNextPieceType('player'),
      this.matchTimer.isActive() ? this.matchTimer.getTimeRemaining() : null,
      spawnTimeRemaining,
      this.isArmyFull('player'),
    );
  }

  // ------------------------------------------------------------------
  // Input handling
  // ------------------------------------------------------------------

  private setupInput(): void {
    this.inputHandler = new InputHandler(this, this.board, {
      getAllPieceStates: () => this.pieceManager.getAllPieceStates(),
      getPieceById: (id: string) => this.pieceManager.getPieceById(id),
      getCellSize: () => CELL_SIZE,
      getCellH: () => CELL_H,
      getBoardOffset: () => ({ x: this.boardOriginX, y: this.boardOriginY }),
      getDragThreshold: () => this.gameSettings?.dragThreshold ?? 8,

      // State queries
      isBattlePlaying: () => this.battleState === 'PLAYING',
      isTargetingMode: () => this.abilityEffectHandler.isTargetingMode,
      handleTargetingClick: (worldX: number, worldY: number) => {
        this.abilityEffectHandler.handleTargetingClick(worldX, worldY);
      },

      // Tutorial gating
      getTutorialAllowedTarget: () =>
        this.tutorialManager?.isActive()
          ? this.tutorialManager.getAllowedInteractionTarget()
          : null,
      getLastMovedPieceId: () => this.pieceManager.lastMovedPieceId,
      isTutorialActive: () =>
        !!(this.tutorialManager && this.tutorialManager.isActive()),
      onTutorialDragStart: () => {
        if (this.tutorialManager?.isActive()) {
          this.tutorialManager.onPlayerInteracting();
        }
      },
      onTutorialDragEnd: () => {
        if (this.tutorialManager?.isActive()) {
          this.tutorialManager.onPlayerStoppedInteracting();
        }
      },
      clearTutorialHighlights: () => {
        this.spotlightResolver.clearTutorialMoveHighlights();
      },
      filterMoves: (piece: Piece, moves: GridPos[], allStates: PieceState[]) =>
        this.spotlightResolver.filterTutorialMoves(piece, moves, allStates),

      // Premove
      isPremoveUnlocked: () =>
        this.gameSettings.premoveEnabled
        && this.levelConfig.level >= this.gameSettings.premoveDisabledUntilLevel,

      // Move execution
      executeMove: (pieceId: string, target: GridPos) => {
        const piece = this.pieceManager.getPieceById(pieceId);
        if (piece && piece.pieceState.isAlive) {
          this.pieceManager.executeMove(piece, target);
        }
      },
    });
  }

  // ------------------------------------------------------------------
  // Intro tips
  // ------------------------------------------------------------------

  private showNextIntroTip(): void {
    if (this.introTipIndex >= this.levelConfig.introTips.length) {
      // Done with tips — move to playing
      if (this.levelConfig.tutorialLevel) {
        this.transitionTo('PLAYING');
        this.hud.setBottomBarVisible(false);
        this.bottomBarRevealed = false;
        this.tutorialManager.startTutorial();
      } else {
        this.transitionTo('PLAYING');
      }
      return;
    }

    const tip = this.levelConfig.introTips[this.introTipIndex];
    this.matchTimer.pause();

    // Use tutorial manager to show a spotlight tip
    // Or show a simple popup
    this.showTipPopup(tip.text, tip.spotlightTarget);
  }

  private showTipPopup(text: string, _spotlightTarget: string): void {
    const { width, height } = this.cameras.main;

    // Dark overlay
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.6);
    overlay.fillRect(0, 0, width, height);
    overlay.setDepth(150);

    // Text popup
    const popupText = this.add.text(width / 2, height / 2, text, {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '28px',
      color: '#ffffff',
      align: 'center',
      wordWrap: { width: width * 0.75 },
      backgroundColor: '#111122',
      padding: { x: 28, y: 18 },
    }).setOrigin(0.5).setDepth(151);

    // "Tap to continue" hint
    const hint = this.add.text(width / 2, height / 2 + 70, 'Tap to continue', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '20px',
      color: '#888888',
    }).setOrigin(0.5).setDepth(151);

    // Tap zone
    const zone = this.add.zone(width / 2, height / 2, width, height)
      .setInteractive()
      .setDepth(152);

    zone.once('pointerdown', () => {
      overlay.destroy();
      popupText.destroy();
      hint.destroy();
      zone.destroy();

      this.introTipIndex++;
      this.showNextIntroTip();
    });
  }

  // ------------------------------------------------------------------
  // Tutorial setup
  // ------------------------------------------------------------------

  private setupTutorial(): void {
    this.tutorialManager = new TutorialManager(this);

    if (this.levelConfig.tutorialLevel) {
      // Load tutorial config from cache
      const allTutorialConfigs = this.cache.json.get('tutorialConfig') as { levels?: Record<string, TutorialLevelConfig> } | null;
      const levelKey = String(this.levelConfig.level);
      const tutorialConfig: TutorialLevelConfig = allTutorialConfigs?.levels?.[levelKey] ?? { steps: [] };

      this.tutorialManager.init(
        this.levelConfig,
        tutorialConfig,
        (target: string) => this.spotlightResolver.resolveSpotlightTarget(target),
      );
    } else {
      // Init with empty config
      this.tutorialManager.init(this.levelConfig, { steps: [] });
    }
    // Event wiring is handled by BattleEventWiring.wireAll()
  }

  // ------------------------------------------------------------------
  // HUD setup
  // ------------------------------------------------------------------

  private setupHUD(): void {
    // Initialize tool and ability managers
    this.toolManager = new ToolManager();
    this.toolManager.init();

    this.abilityManager = new AbilityManager();
    this.abilityManager.init();

    this.hud = new BattleHUD(this);
    this.hud.init(this.levelConfig, playerState.highestLevelCompleted, this.toolManager, this.abilityManager);
    // Event wiring is handled by BattleEventWiring.wireAll()
  }

  // ------------------------------------------------------------------
  // Subsystem setup: AbilityEffectHandler
  // ------------------------------------------------------------------

  private setupAbilityEffectHandler(): void {
    this.abilityEffectHandler = new AbilityEffectHandler();
    this.abilityEffectHandler.init({
      scene: this,
      board: this.board,
      pieceManager: this.pieceManager,
      abilityManager: this.abilityManager,
      cellSize: CELL_SIZE,
      cellH: CELL_H,
      boardOriginX: this.boardOriginX,
      boardOriginY: this.boardOriginY,
      onMatchResult: (result) => {
        this.matchResult = result;
        this.transitionTo('CHECKMATE');
      },
      toggleTutorialOverlay: (interacting) => {
        if (!this.tutorialManager?.isActive()) return;
        if (interacting) this.tutorialManager.onPlayerInteracting();
        else this.tutorialManager.onPlayerStoppedInteracting();
      },
      // Rig bomb to hit top-left of enemy cluster in tutorial level 2
      riggedBombTarget: (this.levelConfig.level === 2 && this.levelConfig.tutorialLevel)
        ? { col: 1, row: 3 } : undefined,
    });
  }

  // ------------------------------------------------------------------
  // Subsystem setup: SpotlightResolver
  // ------------------------------------------------------------------

  private setupSpotlightResolver(): void {
    this.spotlightResolver = new SpotlightResolver();
    this.spotlightResolver.init({
      scene: this,
      board: this.board,
      terrainSystem: this.terrainSystem,
      pieceManager: this.pieceManager,
      tutorialManager: this.tutorialManager,
      cellSize: CELL_SIZE,
      cellH: CELL_H,
      boardOriginX: this.boardOriginX,
      boardOriginY: this.boardOriginY,
      getAbilityButtonBounds: (key: string) => this.hud.getAbilityButtonBounds(key),
      getSpawnBarBounds: () => {
        // Fallback spawn bar area estimate
        return new Phaser.Geom.Rectangle(
          16, this.cameras.main.height - 200 + 8,
          this.cameras.main.width - 32, 120,
        );
      },
      rebuildAbilityButtons: () => this.hud.rebuildAbilityButtons(),
    });
  }

  // ------------------------------------------------------------------
  // Subsystem setup: BattleEventWiring
  // ------------------------------------------------------------------

  private setupEventWiring(): void {
    this.eventWiring = new BattleEventWiring();
    this.eventWiring.wireAll({
      pieceManager: this.pieceManager,
      spawnSystem: this.spawnSystem,
      abilityEffectHandler: this.abilityEffectHandler,
      getElapsedTimeSec: () => this.elapsedTimeSec,
      onMatchEnd: (data) => {
        if (this.battleState === 'PLAYING') {
          this.matchResult = (data?.result as 'victory' | 'defeat') ?? 'victory';
          this.transitionTo('CHECKMATE');
        }
      },
      onTimerExpired: () => {
        if (this.battleState === 'PLAYING') {
          this.matchResult = 'defeat';
          this.transitionTo('CHECKMATE');
        }
      },
    });
  }

  // ------------------------------------------------------------------
  // Checkmate / Result
  // ------------------------------------------------------------------

  private onCheckmate(): void {
    this.matchTimer.pause();

    const { width, height } = this.cameras.main;

    // Dim the board
    const dimOverlay = this.add.graphics();
    dimOverlay.fillStyle(0x000000, 0.5);
    dimOverlay.fillRect(0, 0, width, height);
    dimOverlay.setDepth(50);

    // Banner text
    const bannerText = this.matchResult === 'victory' ? 'CHECKMATE!' : 'DEFEATED!';
    const bannerColor = this.matchResult === 'victory' ? '#ffd700' : '#ff4444';

    const banner = this.add.text(width / 2, height / 2, bannerText, {
      fontFamily: 'Luckiest Guy, Fredoka, Arial, sans-serif',
      fontSize: '64px',
      color: bannerColor,
      stroke: '#000000',
      strokeThickness: 6,
    }).setOrigin(0.5).setDepth(51);

    // Animate banner
    banner.setScale(0);
    this.tweens.add({
      targets: banner,
      scaleX: 1,
      scaleY: 1,
      duration: 500,
      ease: 'Back.easeOut',
    });

    // Delay then transition to result
    this.time.delayedCall(2000, () => {
      this.transitionTo('RESULT');
    });
  }

  private onResult(): void {
    eventBus.emit('match_end', {
      result: this.matchResult,
      levelConfig: this.levelConfig,
      elapsedTime: this.elapsedTimeSec,
    });
  }

  // ------------------------------------------------------------------
  // Cleanup
  // ------------------------------------------------------------------

  shutdown(): void {
    // Kill all active tweens to prevent tween leaks on scene restart
    this.tweens.killAll();

    // Clean up input handler (removes listeners + highlights)
    this.inputHandler?.destroy();

    // Destroy subsystems
    this.hud?.destroy();
    this.eventWiring?.cleanup();
    this.abilityEffectHandler?.destroy();
    this.spotlightResolver?.destroy();
    this.tutorialManager?.destroy();
    this.pieceManager?.reset();
    this.matchTimer?.destroy();
    this.spawnSystem?.reset();
    this.waveSystem?.reset();
    this.aiController?.reset();
    this.movementLines?.destroy();
    this.terrainSystem?.destroy();
    this.toolManager?.destroy();
    this.abilityManager?.destroy();
  }
}
